import { useCallback, useRef } from "react";
import { flushSync } from "react-dom";

const ANIMATION_MS = 250;

const siblingBlocks = (block, selector) =>
  Array.from(block.parentElement?.children ?? []).filter((el) => el.matches(selector));

// A transform makes a block paint above its untransformed siblings, which would
// put the ones sliding out of the way in front of the block being moved. Only
// z-index can override that, and only on a positioned element.
const liftAboveSiblings = (el) => {
  el.style.position = "relative";
  el.style.zIndex = "1";
};

const clearAnimation = (el) => {
  el.style.transition = "";
  el.style.transform = "";
  el.style.position = "";
  el.style.zIndex = "";
};

// Animates a list reordered with up/down arrows: every block matched by
// `selector` that changes position glides from where it was into its new slot
// (FLIP), and the clicked one is lifted above the rest while they pass it.
//
// With `compensateScroll` the page also scrolls by however far the clicked
// block jumped, keeping it — and the arrow button under the cursor — fixed in
// the viewport, so only its neighbours appear to move. Worth it for blocks tall
// enough that a swap would throw them off screen; for short rows, leave it off
// and let the clicked block travel.
//
// Relies on the list being rendered with stable keys, so React moves the
// blocks' DOM nodes instead of recreating them and the references stay valid
// across the re-render.
export default function useReorderAnimation(selector, { compensateScroll = true } = {}) {
  const settleTimer = useRef(null);

  return useCallback((event, reorder) => {
    const block = event?.currentTarget?.closest(selector);
    if (!block) {
      reorder();
      return;
    }

    // A block still mid-glide reports a transformed rect, so land any running
    // animation first: a fast second click then measures real positions
    // instead of compounding the offset.
    const blocks = siblingBlocks(block, selector);
    clearTimeout(settleTimer.current);
    blocks.forEach(clearAnimation);

    const topsBefore = new Map(blocks.map((el) => [el, el.getBoundingClientRect().top]));

    flushSync(reorder);

    if (compensateScroll) {
      window.scrollBy(0, block.getBoundingClientRect().top - topsBefore.get(block));
    }

    // First half of FLIP: pin every moved block back to where it was. With the
    // scroll compensation on, the clicked block usually lands on a zero shift
    // — the scroll already held it in place — so it never animates.
    const moved = [];
    topsBefore.forEach((topBefore, el) => {
      if (!el.isConnected) return;

      const shift = topBefore - el.getBoundingClientRect().top;
      if (Math.abs(shift) < 1) return;

      el.style.transition = "none";
      el.style.transform = `translateY(${shift}px)`;
      moved.push(el);
    });

    if (moved.length === 0) return;

    liftAboveSiblings(block);

    // Commit the inverted positions before transitioning out of them,
    // otherwise the browser collapses both styles into one paint and nothing
    // animates.
    void document.body.offsetHeight;

    requestAnimationFrame(() => {
      moved.forEach((el) => {
        el.style.transition = `transform ${ANIMATION_MS}ms ease-out`;
        el.style.transform = "";
      });
    });

    // The lifted block may have no transition of its own to wait on, so the
    // whole list is cleaned up on a timer once the glide is over.
    settleTimer.current = setTimeout(() => {
      blocks.forEach(clearAnimation);
    }, ANIMATION_MS + 50);
  }, [selector, compensateScroll]);
}
